import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  HttpLink,
  InMemoryCache,
  RequestHandler,
  split,
} from '@apollo/client';
import { PropsWithChildren, useMemo } from 'react';
import fetch from 'cross-fetch';
import { RetryLink } from '@apollo/client/link/retry';
import { useCookies } from 'react-cookie';
import { MimicTarget } from '../../types/MimicTarget';

// Apollo's HttpLink throws a ServerError (with a `statusCode`) for any
// non-2xx HTTP response. Client errors (4xx) are not transient -- e.g. a 403
// from an authorization check will fail identically on every retry -- so
// only retry everything else (real network failures, 5xx, timeouts, etc).
export function isRetryableError(error: unknown): boolean {
  const statusCode = (error as { statusCode?: number } | undefined)?.statusCode;
  return !(
    typeof statusCode === 'number' &&
    statusCode >= 400 &&
    statusCode < 500
  );
}

export function createRequestHandlerFromToken(
  token: string | undefined,
  mimicUser: string | undefined,
  mimicCompany: string | undefined
): RequestHandler {
  return (operation, forward) => {
    const { skipMimic } = operation.getContext() as { skipMimic?: boolean };
    const mimicHeaders =
      !skipMimic && mimicUser && mimicCompany
        ? {
            'mimic-user': mimicUser,
            'mimic-company-id': mimicCompany,
          }
        : {};
    const headers = {
      authorization: `Bearer ${token}`,
      environment: process.env['NX_PUBLIC_APP_ENV'],
      ...mimicHeaders,
    };

    operation.setContext({ headers });

    return token ? forward(operation) : null;
  };
}

export type SdnApolloProviderProps = PropsWithChildren<{
  mimicState: MimicTarget | undefined;
}>;

export function SdnApolloProvider({
  children,
  mimicState,
}: SdnApolloProviderProps) {
  const [cookies] = useCookies(['access_token']);
  const token = cookies.access_token;

  const sdnClient = useMemo(() => {
    const retryLink = new RetryLink({
      delay: {
        initial: 300,
        max: Infinity,
        jitter: true,
      },
      attempts: {
        max: 5,
        retryIf: isRetryableError,
      },
    });
    const headersContext = new ApolloLink(
      createRequestHandlerFromToken(
        token,
        mimicState?.email,
        mimicState?.companyId
      )
    );

    const daapHttpLink = new HttpLink({
      uri: process.env['NX_PUBLIC_APP_DAAP_API_BASE_URL'],
      fetch,
    });

    const sdnHttpLink = new HttpLink({
      uri: process.env['NX_PUBLIC_APP_SDN_API_BASE_URL'],
      fetch,
    });

    // Route sdno* operations (e.g. sdnoAuthPayload) to the SDN API;
    // everything else goes to the DAAP API.
    const routedLink = split(
      (operation) => /^sdno/i.test(operation.operationName),
      sdnHttpLink,
      daapHttpLink
    );

    const additiveLink = from([headersContext, retryLink, routedLink]);
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: additiveLink,
    });
  }, [mimicState?.email, mimicState?.companyId, token]);

  return <ApolloProvider client={sdnClient}>{children}</ApolloProvider>;
}
